import { ErrorScreen } from '@/components/Error';
import { isOptimizerTimeoutError } from '@/optimizer/helpers/withOptimizerTimeout';
import { dashboardRootRoute, toPath } from '@/utils/routes';
import { Button, Card } from '@analytical-alley/ui';
import {
    useNavigate,
    useParams,
    useSearch,
    type ErrorComponentProps,
} from '@tanstack/react-router';

const CAMPAIGN_OPTIMIZATION_ROUTE_ID =
    '/_org/project/$projectId/_dashboards/campaign-optimization/$campaignId';

/**
 * Route-level error component for the Campaign Optimization dashboard.
 *
 * The page's `InitialTimeoutEscalation` re-throws a pre-commit failure
 * (bootstrap error, or an optimiser timeout before the first run committed)
 * during render, so it reaches this route error boundary. The generic
 * {@link ErrorScreen} only offers "Reload", which re-runs the same
 * optimization and times out again — trapping the user in a loop with a
 * technical message.
 *
 * For this *expected* failure we show a friendly, plain-language screen whose
 * primary action navigates the user *out* of the dashboard (back to the
 * campaign list) instead of reloading. Any other (unexpected) error falls
 * through to the generic screen unchanged.
 */
export const CampaignOptimizationErrorScreen = ({
    error,
    reset,
}: ErrorComponentProps) => {
    const navigate = useNavigate();
    const { projectId } = useParams({ from: CAMPAIGN_OPTIMIZATION_ROUTE_ID });
    const { dashboardId } = useSearch({ from: CAMPAIGN_OPTIMIZATION_ROUTE_ID });

    if (!isOptimizerTimeoutError(error)) {
        return <ErrorScreen error={error} reset={reset} />;
    }

    const goToCampaigns = () => {
        void navigate({
            to: toPath(dashboardRootRoute, '/campaign-planner'),
            params: { projectId },
            search: { dashboardId },
        });
    };

    return (
        <Card className="m-14">
            <div className="grid-cols-2 gap-8 content-center py-8 px-4 mx-auto max-w-(--breakpoint-xl) md:grid lg:py-16 lg:px-6">
                <div className="self-center">
                    <p className="mb-4 text-3xl tracking-tight font-bold text-gray-900 lg:mb-6 md:text-4xl dark:text-white">
                        Optimization is taking too long
                    </p>
                    <p className="mb-8 text-gray-500 dark:text-gray-400">
                        We couldn&apos;t finish optimizing this campaign right
                        now. This usually clears up on its own. Please head back
                        to your campaigns and try opening it again in a moment.
                        If it keeps happening, contact support.
                    </p>
                    <Button size="sm" color="blue" onClick={goToCampaigns}>
                        Back to campaigns
                    </Button>
                </div>
            </div>
        </Card>
    );
};
